<template>
    <div>
        <!-- House number input field -->
        <div :class="houseNumberInputClass">
            <label class="input__label" for="shipping-address-housenumber"
                v-text="$t('DELIVERY_ADDRESS_FORM_HOUSE_NUMBER')"></label>
            <input
                id="shipping-address-housenumber"
                name="shipping-address-housenumber"
                autocomplete="shipping address-housenumber"
                type="text"
                class="input__field"
                v-model="address.houseNumber">

            <!-- Validation -->
            <p class="input__message" v-if="showHouseNumberRequiredValidationMessage"
                v-text="$t('DELIVERY_ADDRESS_PLEASE_PUT_IN_HOUSE_NUMBER')"></p>
        </div>

        <!-- Address line 1 input field -->
        <div :class="addressLine1InputClass">
            <label class="input__label ADDRESS_1_REQUIRED" for="shipping-address-line1"
                v-text="$t('DELIVERY_ADDRESS_FIRST_LINE')"></label>
            <input
                id="shipping-address-line1"
                name="shipping-address-line1"
                autocomplete="shipping address-line1"
                type="text"
                class="input__field"
                v-model="address.addressLine1">

            <!-- Validation -->
            <p class="input__message" v-if="showAddressLine1RequiredValidationMessage"
                v-text="$t('DELIVERY_ADDRESS_PLEASE_PUT_IN')"></p>
        </div>

        <!-- Address line 2 input field -->
        <div :class="addressLine2InputClass">
            <label class="input__label" for="shipping-address-line2"
                v-text="$t('DELIVERY_ADDRESS_SECOND_LINE')"></label>
            <input
                id="shipping-address-line2"
                name="shipping-address-line2"
                autocomplete="shipping address-line2"
                type="text"
                class="input__field"
                v-model="address.addressLine2"
                :placeholder="$t('DELIVERY_ADDRESS_OPTIONAL')">
        </div>

        <!-- Address city input field -->
        <div :class="addressCityInputClass">
            <label class="input__label ADDRESS_CITY_REQUIRED" for="shipping-city"
                v-text="$t('DELIVERY_ADDRESS_TOWN_OR_CITY')"></label>
            <input
                id="shipping-city"
                name="shipping-city"
                autocomplete="shipping address-level2"
                type="text"
                class="input__field"
                v-model="address.city">

            <!-- Validation -->
            <p class="input__message" v-if="showAddressCityRequiredValidationMessage"
                v-text="$t('DELIVERY_ADDRESS_PLEASE_PUT_TOWN_OR_CITY')"></p>
        </div>

        <!-- Post code input field -->
        <div :class="postcodeInputClass">
            <label class="input__label POSTCODE_REQUIRED" for="shipping-postcode"
                v-text="$t('DELIVERY_POSTCODE')"></label>
            <input
                id="shipping-postcode"
                name="shipping-postcode"
                autocomplete="shipping postcode"
                type="text"
                class="input__field"
                v-model="address.postcode">
        </div>
    </div>
</template>

<script>
import required from 'vuelidate/src/validators/required';
import { isPostcodeDeliverable, isValidPostcode } from '@/validators/PostcodeValidator';

export default {
    name: 'GiftingAddressForm',
    props: {
        enableDeliveryWaitingList: {
            default: false,
            type: Boolean,
        },
        value: {
            default: null,
            type: Object,
        },
    },
    data() {
        return {
            postcodeComponent: null,
            apiValidation: {
                postcode: {
                    isPostcodeDeliverable: true,
                },
            },
            address: {
                houseNumber: null,
                addressLine1: null,
                addressLine2: null,
                city: null,
                postcode: null,
            },
            isDeliveryWaitingListModalVisible: false,
        };
    },
    computed: {
        addressCityInputClass() {
            if (this.address.city === null) {
                return 'input';
            }

            return this.isAddressCityValid ? 'input input--success' : 'input input--invalid';
        },
        houseNumberInputClass() {
            if (this.address.houseNumber === null) {
                return 'input';
            }

            return this.isHouseNumberValid ? 'input input--success' : 'input input--invalid';
        },
        addressLine1InputClass() {
            if (this.address.addressLine1 === null) {
                return 'input';
            }

            return this.isAddressLine1Valid ? 'input input--success' : 'input input--invalid';
        },
        addressLine2InputClass() {
            return this.address && this.address.addressLine2 && this.address.addressLine2.length > 0 ? 'input input--success' : 'input';
        },
        postcodeInputClass() {
            if (this.address.postcode === null) {
                return 'input';
            }

            return this.isPostcodeValid ? 'input input--success' : 'input input--invalid';
        },
        isAddressCityValid() {
            return !this.$v.address.city.$invalid;
        },
        isAddressLine1Valid() {
            return !this.$v.address.addressLine1.$invalid;
        },
        isHouseNumberValid() {
            return !this.$v.address.houseNumber.$invalid;
        },
        isAllFormFieldsValid() {
            return !this.$v.$invalid;
        },
        isPostcodeValid() {
            return this.$v.address.postcode.required && this.$v.address.postcode.isValidPostcode;
        },
        // postcodeLookupField() {
        //     const path = {
        //         'en': import('@/components/forms/fields/PostcodeLookupField'),
        //         'de': import('@/components/forms/fields/de/PostcodeLookupField'),
        //         'nl': import('@/components/forms/fields/de/PostcodeLookupField'),
        //     };
        //     path[process.env.VUE_APP_I18N_LOCALE].then(module => module.default).then(response => {
        //         this.postcodeComponent = response;
        //     });

        //     return this.postcodeComponent;
        // },
        showAddressCityRequiredValidationMessage() {
            return this.address.city !== null && !this.$v.address.city.required;
        },
        showAddressLine1RequiredValidationMessage() {
            return this.address.addressLine1 !== null && !this.$v.address.addressLine1.required;
        },
        showHouseNumberRequiredValidationMessage() {
            return this.address.houseNumber !== null && !this.$v.address.houseNumber.required;
        },
        showIsPostcodeDeliverableValidationMessage() {
            return !this.$v.address.postcode.isPostcodeDeliverable && !this.enableDeliveryWaitingList;
        },
    },
    methods: {
        async canDeliveryToPostcode(postcode) {
            if (this.isPostcodeValid) {
                let status = await isPostcodeDeliverable(postcode);

                this.updateApiValidationStatus(status);
            }
        },
        clearAddress(value) {
            if (value) {
                this.address.houseNumber = null;
                this.address.addressLine1 = null;
                this.address.addressLine2 = null;
                this.address.city = null;
            }
        },
        updateApiValidationStatus(status) {
            this.apiValidation.postcode.isPostcodeDeliverable = status;
        },
        updateInput(deliveryAddress) {
            this.$v.$touch();

            this.$emit('input', deliveryAddress);
            this.$emit('update:validation', this.isAllFormFieldsValid);
        },
        updateSelectedAddress(selectedAddress) {
            if (selectedAddress) {
                this.address.houseNumber = selectedAddress.houseNumber;
                this.address.addressLine1 = selectedAddress.addressline1;
                this.address.addressLine2 = selectedAddress.addressline2;
                this.address.city = selectedAddress.posttown;
            } else {
                this.address.houseNumber = null;
                this.address.addressLine1 = null;
                this.address.addressLine2 = null;
                this.address.city = null;
            }
        },
    },
    validations: {
        address: {
            houseNumber: {
                required,
            },
            addressLine1: {
                required,
            },
            city: {
                required,
            },
            postcode: {
                required,
                isValidPostcode,
                isPostcodeDeliverable() {
                    return this.apiValidation.postcode.isPostcodeDeliverable;
                },
            },
        },
    },
    watch: {
        address: {
            handler: function(deliveryAddress) {
                this.updateInput(deliveryAddress);
            },
            deep: true,
        },
        'address.postcode'(value) {
            this.canDeliveryToPostcode(value);
        },
    },
};
</script>
